/**
 * Admin claim verifier — defence-in-depth for admin-gated UI.
 *
 * Reads `request.auth.token.role` directly from a force-refreshed ID token.
 * Bypasses any client-side state (Zustand store, localStorage, etc.) — those
 * are UI hints only. The token claim is set by the Cloud Function that grants
 * admin (signed by Google), so non-spoofable from the browser console.
 *
 * Use anywhere AdminPanel branches on admin status. Firestore rules also
 * enforce the same claim server-side; this helper is the UI's first line.
 */
import { auth } from '../firebase';

/** Returns true iff the currently-signed-in user has the admin role claim. */
export async function requireAdminClaim(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    // forceRefresh=true bypasses the local cache so a revoked admin can't
    // keep a stale "admin" claim alive past role removal.
    const result = await user.getIdTokenResult(true);
    return result.claims.role === 'admin';
  } catch (e) {
    console.warn('requireAdminClaim: token refresh failed', e);
    return false;
  }
}
